import React, { useEffect, useState } from "react";
import { ChatAndCallPackageInterface } from "../../interfaces/chat-and-call-package.interface";
import { get, remove } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";
import { MdAdd, MdEdit, MdDeleteOutline } from "react-icons/md";
import ChatAndCallPackageModal from "./ChatAndCallPackageModal";

const ChatAndCallPackageSettings = () => {
  const [packages, setPackages] = useState<ChatAndCallPackageInterface[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<ChatAndCallPackageInterface | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all chat and call packages
  const fetchPackages = async () => {
    try {
      setIsLoading(true);
      const response = await get("/admin/chat-call/chat-and-call-packages");
      setPackages(response || []);
    } catch (error) {
      errorToast("Failed to fetch chat and call packages");
      console.error("Error fetching packages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  // Handle delete
  const handleDelete = async (packageId: string) => {
    if (!window.confirm("Are you sure you want to delete this package?")) {
      return;
    }

    try {
      setIsLoading(true);
      await remove(`/admin/chat-call/chat-and-call-packages/${packageId}`);
      successToast("Package deleted successfully");
      fetchPackages();
    } catch (error) {
      errorToast("Failed to delete package");
      console.error("Error deleting package:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Open modal for create
  const openModal = () => {
    setIsEdit(false);
    setEditData(null);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const handleEdit = (packageData: ChatAndCallPackageInterface) => {
    setIsEdit(true);
    setEditData(packageData);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
    setIsEdit(false);
  };

  // Handle modal success (refresh list)
  const handleModalSuccess = () => {
    fetchPackages();
    closeModal();
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-center justify-center p-4 flex-grow min-h-screen">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-semibold text-yellow-500">
            Chat and Call Package Settings
          </h1>
          <button
            onClick={openModal}
            className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-400 transition font-medium flex items-center gap-2"
          >
            <MdAdd className="text-xl" />
            Add New Package
          </button>
        </div>

        {isLoading && packages.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Loading packages...
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No packages found. Click "Add New Package" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Custom Table */}
            <table className="w-full border-collapse">
              {/* Table Header */}
              <thead>
                <tr className="bg-gray-700 border-b-2 border-yellow-500">
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Balance
                  </th>
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Price (INR)
                  </th>
                  <th className="px-6 py-4 text-center text-yellow-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              {/* Table Body */}
              <tbody>
                {packages.map((pkg, index) => (
                  <tr
                    key={pkg._id || index}
                    className={`border-b border-gray-700 hover:bg-gray-700/50 transition ${
                      index % 2 === 0 ? "bg-gray-800" : "bg-gray-800/50"
                    }`}
                  >
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      {pkg.balance}
                    </td>
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      ₹{pkg.price}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-3">
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="p-2 rounded-full bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 transition cursor-pointer"
                          title="Edit Package"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => pkg._id && handleDelete(pkg._id)}
                          className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition cursor-pointer"
                          title="Delete Package"
                        >
                          <MdDeleteOutline className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Package Count */}
            <div className="mt-4 text-sm text-gray-400 text-right">
              Total Packages: {packages.length}
            </div>
          </div>
        )}

        {/* Create/Update Modal */}
        <ChatAndCallPackageModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          isEdit={isEdit}
          editData={editData}
          onSuccess={handleModalSuccess}
        />
      </div>
    </div>
  );
};

export default ChatAndCallPackageSettings;
